// src/pages/CRM/SFA/OpportunityForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, TextField, Button, IconButton,
  FormControl, InputLabel, Select, MenuItem, Autocomplete,
  InputAdornment, Divider, Alert, CircularProgress, Chip, Stack
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useSnackbar } from 'notistack';
import { hasPermission } from '../../../utils/permissionUtils';
import { opportunityService } from './sfaService';
import axiosInstance from '../../../utils/axiosInstance';
import {
  OpportunityStage, OpportunityStageLabels, StageProbabilities,
  OpportunityType, OpportunityTypeLabels,
  ForecastCategory, ForecastCategoryLabels,
  LeadSource, LeadSourceLabels,
  VisibilityScope, VisibilityScopeLabels,
  Currencies, AllStages
} from './sfaEnums';

const OpportunityForm = () => {
  const { chatbotId, opportunityId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(opportunityId) && opportunityId !== 'new';

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    account_id: null,
    contact_id: null,
    amount: '',
    currency: 'USD',
    probability: 10,
    stage: OpportunityStage.PROSPECTING,
    opportunity_type: OpportunityType.NEW_BUSINESS,
    forecast_category: ForecastCategory.PIPELINE,
    visibility_scope: VisibilityScope.ORGANIZATION,
    close_date: null,
    next_step: '',
    next_step_date: null,
    primary_competitor: '',
    lead_source: '',
    campaign_id: '',
    tags: []
  });

  // Lookup data
  const [accounts, setAccounts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // Permissions
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    document.title = isEdit ? 'Edit Opportunity' : 'New Opportunity';
    checkPermissions();
    loadLookupData();
    if (isEdit) {
      loadOpportunity();
    }
  }, [opportunityId]);

  useEffect(() => {
    // Load contacts when account changes
    if (selectedAccount) {
      loadContacts(selectedAccount.id);
    }
  }, [selectedAccount]);

  const checkPermissions = async () => {
    try {
      const modifyPerm = await hasPermission('crm', 'add|edit');
      setCanModify(modifyPerm);
    } catch (err) {
      console.error('Error checking permissions:', err);
    }
  };

  const loadOpportunity = async () => {
    setLoading(true);
    try {
      const response = await opportunityService.getById(opportunityId);
      if (response.success) {
        const opp = response.data;
        setFormData({
          name: opp.name || '',
          description: opp.description || '',
          account_id: opp.account_id,
          contact_id: opp.contact_id,
          amount: opp.amount || '',
          currency: opp.currency || 'USD',
          probability: opp.probability || 10,
          stage: opp.stage || OpportunityStage.PROSPECTING,
          opportunity_type: opp.opportunity_type || OpportunityType.NEW_BUSINESS,
          forecast_category: opp.forecast_category || ForecastCategory.PIPELINE,
          visibility_scope: opp.visibility_scope || VisibilityScope.ORGANIZATION,
          close_date: opp.close_date ? new Date(opp.close_date) : null,
          next_step: opp.next_step || '',
          next_step_date: opp.next_step_date ? new Date(opp.next_step_date) : null,
          primary_competitor: opp.primary_competitor || '',
          lead_source: opp.lead_source || '',
          campaign_id: opp.campaign_id || '',
          tags: opp.tags || []
        });

        if (opp.account) {
          setSelectedAccount(opp.account);
        }
        if (opp.contact) {
          setSelectedContact(opp.contact);
        }
      }
    } catch (err) {
      enqueueSnackbar('Failed to load opportunity', { variant: 'error' });
      navigate(`/chatbot/${chatbotId}/crm/opportunities`);
    } finally {
      setLoading(false);
    }
  };

  const loadLookupData = async () => {
    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};
      
      // Load accounts
      const accountsResponse = await axiosInstance.get('/api/crm/accounts', {
        params: { ...params, per_page: 100 }
      });
      if (accountsResponse.data.success) {
        setAccounts(accountsResponse.data.data);
      }
    } catch (err) {
      console.error('Error loading lookup data:', err);
    }
  };

  const loadContacts = async (accountId) => {
    try {
      const response = await axiosInstance.get('/api/crm/contacts', {
        params: { account_id: accountId, per_page: 100 }
      });
      if (response.data.success) {
        setContacts(response.data.data);
      }
    } catch (err) {
      console.error('Error loading contacts:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Auto-update probability when stage changes
    if (field === 'stage' && StageProbabilities[value] !== undefined) {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        probability: StageProbabilities[value]
      }));
    }
  };

  const handleAccountChange = (event, newValue) => {
    setSelectedAccount(newValue);
    setFormData(prev => ({
      ...prev,
      account_id: newValue?.id || null,
      contact_id: null
    }));
    setSelectedContact(null);
    setContacts([]);
  };

  const handleContactChange = (event, newValue) => {
    setSelectedContact(newValue);
    setFormData(prev => ({ ...prev, contact_id: newValue?.id || null }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name) {
      enqueueSnackbar('Opportunity name is required', { variant: 'error' });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        close_date: formData.close_date ? formData.close_date.toISOString() : null,
        next_step_date: formData.next_step_date ? formData.next_step_date.toISOString() : null
      };

      if (chatbotId) {
        payload.chatbot_id = chatbotId;
      }

      let response;
      if (isEdit) {
        response = await opportunityService.update(opportunityId, payload);
        enqueueSnackbar('Opportunity updated successfully', { variant: 'success' });
      } else {
        response = await opportunityService.create(payload);
        enqueueSnackbar('Opportunity created successfully', { variant: 'success' });
      }

      navigate(`/chatbot/${chatbotId}/crm/opportunities/${response.data.id}`);
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || 'Failed to save opportunity', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canModify && !isEdit) {
    return (
      <Alert severity="error" sx={{ mt: 10, mx: 3 }}>
        You don't have permission to create opportunities.
      </Alert>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3, mt: 10 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              {isEdit ? 'Edit Opportunity' : 'New Opportunity'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isEdit ? 'Update opportunity details' : 'Create a new sales opportunity'}
            </Typography>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Main Details */}
            <Grid item xs={12} md={8}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Opportunity Details
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Opportunity Name"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="e.g., Enterprise Software Deal - Acme Corp"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={accounts}
                      getOptionLabel={(option) => option.name || ''}
                      value={selectedAccount}
                      onChange={handleAccountChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Account"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <BusinessIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      options={contacts}
                      getOptionLabel={(option) =>
                        option ? `${option.first_name} ${option.last_name}` : ''
                      }
                      value={selectedContact}
                      onChange={handleContactChange}
                      disabled={!selectedAccount}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Primary Contact"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <PersonIcon color="action" sx={{ ml: 1, mr: 0.5 }} />
                                {params.InputProps.startAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Description"
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      placeholder="Describe the opportunity, customer needs, and key requirements..."
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Deal Information */}
              <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Deal Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={formData.amount}
                      onChange={(e) => handleChange('amount', e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <MoneyIcon color="action" />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={formData.currency}
                        label="Currency"
                        onChange={(e) => handleChange('currency', e.target.value)}
                      >
                        {Currencies.map((curr) => (
                          <MenuItem key={curr.code} value={curr.code}>
                            {curr.symbol} - {curr.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Probability"
                      type="number"
                      value={formData.probability}
                      onChange={(e) => handleChange('probability', parseInt(e.target.value) || 0)}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>
                      }}
                      inputProps={{ min: 0, max: 100 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Stage</InputLabel>
                      <Select
                        value={formData.stage}
                        label="Stage"
                        onChange={(e) => handleChange('stage', e.target.value)}
                      >
                        {AllStages.map((stage) => (
                          <MenuItem key={stage} value={stage}>
                            {OpportunityStageLabels[stage]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={formData.opportunity_type}
                        label="Type"
                        onChange={(e) => handleChange('opportunity_type', e.target.value)}
                      >
                        {Object.entries(OpportunityTypeLabels).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Close Date"
                      value={formData.close_date}
                      onChange={(date) => handleChange('close_date', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Forecast Category</InputLabel>
                      <Select
                        value={formData.forecast_category}
                        label="Forecast Category"
                        onChange={(e) => handleChange('forecast_category', e.target.value)}
                      >
                        {Object.entries(ForecastCategoryLabels).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Visibility</InputLabel>
                      <Select
                        value={formData.visibility_scope}
                        label="Visibility"
                        onChange={(e) => handleChange('visibility_scope', e.target.value)}
                      >
                        {Object.entries(VisibilityScopeLabels).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>

              {/* Additional Info */}
              <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Additional Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Next Step"
                      value={formData.next_step}
                      onChange={(e) => handleChange('next_step', e.target.value)}
                      placeholder="e.g., Schedule product demo"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <DatePicker
                      label="Next Step Date"
                      value={formData.next_step_date}
                      onChange={(date) => handleChange('next_step_date', date)}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Primary Competitor"
                      value={formData.primary_competitor}
                      onChange={(e) => handleChange('primary_competitor', e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Lead Source</InputLabel>
                      <Select
                        value={formData.lead_source}
                        label="Lead Source"
                        onChange={(e) => handleChange('lead_source', e.target.value)}
                      >
                        <MenuItem value="">None</MenuItem>
                        {Object.entries(LeadSourceLabels).map(([key, label]) => (
                          <MenuItem key={key} value={key}>
                            {label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Campaign ID"
                      value={formData.campaign_id}
                      onChange={(e) => handleChange('campaign_id', e.target.value)}
                      placeholder="Optional: Link to marketing campaign"
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, position: 'sticky', top: 80 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Deal Value
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary">
                      {formData.amount
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: formData.currency
                          }).format(formData.amount)
                        : '-'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Weighted Value
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formData.amount
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: formData.currency
                          }).format(formData.amount * (formData.probability / 100))
                        : '-'}
                    </Typography>
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Stage
                    </Typography>
                    <Chip
                      label={OpportunityStageLabels[formData.stage]}
                      sx={{ mt: 0.5 }}
                      color="primary"
                    />
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Probability
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formData.probability}%
                    </Typography>
                  </Box>

                  {formData.close_date && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Expected Close
                      </Typography>
                      <Typography variant="body1">
                        {formData.close_date.toLocaleDateString()}
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Divider sx={{ my: 3 }} />

                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : isEdit ? 'Update Opportunity' : 'Create Opportunity'}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </LocalizationProvider>
  );
};

export default OpportunityForm;